import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { BuilderNode, BuilderComponentType } from '../interfaces/builder-screen.models';

export interface PropertySuggestion {
  id: string;
  title: string;
  description: string;
  category: 'improvement' | 'accessibility' | 'ux' | 'performance' | 'validation';
  impact: 'high' | 'medium' | 'low';
  confidence: number; // 0-100
  properties: Record<string, any>;
  reasoning: string;
  learnMoreUrl?: string;
}

export interface ComponentContext {
  componentType: BuilderComponentType;
  currentProperties: Record<string, any>;
  siblingComponents: BuilderNode[];
  parentComponent?: BuilderNode;
  userBehavior: UserBehaviorData;
  formContext: FormContext;
}

export interface UserBehaviorData {
  recentActions: string[];
  commonPatterns: string[];
  preferredStyles: Record<string, any>;
  errorHistory: string[];
}

export interface FormContext {
  totalComponents: number;
  hasValidation: boolean;
  theme: 'light' | 'dark' | 'auto';
  purpose: 'form' | 'display' | 'navigation' | 'mixed';
}

@Injectable({
  providedIn: 'root',
})
export class AIPropertySuggestionsService {
  private suggestionsCache = new Map<string, PropertySuggestion[]>();
  private userBehavior = new BehaviorSubject<UserBehaviorData>({
    recentActions: [],
    commonPatterns: [],
    preferredStyles: {},
    errorHistory: [],
  });

  constructor() {
    this.loadUserBehavior();
  }

  // Obtém sugestões alimentadas por IA para um componente
  getSuggestions(component: BuilderNode, context: ComponentContext): Observable<PropertySuggestion[]> {
    const cacheKey = this.generateCacheKey(component, context);
    
    // Verifica cache primeiro
    if (this.suggestionsCache.has(cacheKey)) {
      return of(this.suggestionsCache.get(cacheKey)!);
    }

    // Simula processamento de IA com delay realista
    return this.generateSuggestions(component, context).pipe(
      delay(800), // Simula tempo de processamento de IA
      map(suggestions => {
        // Armazena resultados em cache
        this.suggestionsCache.set(cacheKey, suggestions);
        return suggestions;
      })
    );
  }

  // Aplica uma sugestão a um componente
  applySuggestion(suggestion: PropertySuggestion, component: BuilderNode): BuilderNode {
    const updatedProps = {
      ...component.props,
      ...suggestion.properties,
    };

    // Rastreia comportamento do usuário
    this.trackUserAction(`applied_suggestion_${suggestion.category}`);

    return {
      ...component,
      props: updatedProps,
    };
  }

  // Descarta uma sugestão (para aprendizado)
  dismissSuggestion(suggestionId: string, reason: 'not_relevant' | 'already_done' | 'not_interested') {
    this.trackUserAction(`dismissed_suggestion_${reason}`);
  }

  // Obtém sugestões por categoria
  getSuggestionsByCategory(
    component: BuilderNode, 
    context: ComponentContext, 
    category: PropertySuggestion['category']
  ): Observable<PropertySuggestion[]> {
    return this.getSuggestions(component, context).pipe(
      map(suggestions => suggestions.filter(s => s.category === category))
    );
  }

  // Obtém apenas sugestões de alto impacto
  getHighImpactSuggestions(component: BuilderNode, context: ComponentContext): Observable<PropertySuggestion[]> {
    return this.getSuggestions(component, context).pipe(
      map(suggestions => suggestions.filter(s => s.impact === 'high' && s.confidence > 80))
    );
  }

  private generateSuggestions(component: BuilderNode, context: ComponentContext): Observable<PropertySuggestion[]> {
    const suggestions: PropertySuggestion[] = [];
    const props = component.props as any;

    // Sugestões de acessibilidade
    suggestions.push(...this.generateAccessibilitySuggestions(component, context));
    
    // Sugestões de melhoria de UX
    suggestions.push(...this.generateUXSuggestions(component, context));
    
    // Sugestões de validação
    suggestions.push(...this.generateValidationSuggestions(component, context));
    
    // Sugestões de performance
    suggestions.push(...this.generatePerformanceSuggestions(component, context));
    
    // Sugestões de consistência de estilo
    suggestions.push(...this.generateStyleSuggestions(component, context));

    // Ordena por confiança e impacto
    const sortedSuggestions = suggestions
      .sort((a, b) => {
        const scoreA = this.calculateSuggestionScore(a);
        const scoreB = this.calculateSuggestionScore(b);
        return scoreB - scoreA;
      })
      .slice(0, 5); // Limita às 5 melhores sugestões

    return of(sortedSuggestions);
  }

  private generateAccessibilitySuggestions(component: BuilderNode, context: ComponentContext): PropertySuggestion[] {
    const suggestions: PropertySuggestion[] = [];
    const props = component.props as any;

    // Labels ausentes
    if (['text', 'textarea', 'number'].includes(component.type) && !props.label) {
      suggestions.push({
        id: `accessibility-label-${component.id}`,
        title: 'Add Accessibility Label',
        description: 'This input field is missing a label, which makes it difficult for screen readers.',
        category: 'accessibility',
        impact: 'high',
        confidence: 95,
        properties: { label: this.suggestLabelText(component.type) },
        reasoning: 'Screen readers need labels to understand form fields. This improves accessibility for visually impaired users.',
        learnMoreUrl: 'https://www.w3.org/WAI/WCAG21/Understanding/labels-or-instructions.html',
      });
    }

    // Contraste de cor ruim
    if (props.backgroundColor && props.textColor) {
      const contrast = this.calculateColorContrast(props.backgroundColor, props.textColor);
      if (contrast < 4.5) {
        suggestions.push({
          id: `accessibility-contrast-${component.id}`,
          title: 'Improve Color Contrast',
          description: 'The current color combination may not meet accessibility standards.',
          category: 'accessibility',
          impact: 'medium',
          confidence: 85,
          properties: this.suggestBetterColors(props.backgroundColor, props.textColor),
          reasoning: 'WCAG requires a contrast ratio of at least 4.5:1 for normal text to ensure readability.',
          learnMoreUrl: 'https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html',
        });
      }
    }

    return suggestions;
  }

  private generateUXSuggestions(component: BuilderNode, context: ComponentContext): PropertySuggestion[] {
    const suggestions: PropertySuggestion[] = [];
    const props = component.props as any;

    // Texto de placeholder ausente
    if (['text', 'textarea'].includes(component.type) && !props.placeholder) {
      suggestions.push({
        id: `ux-placeholder-${component.id}`,
        title: 'Add Helpful Placeholder',
        description: 'Placeholder text helps users understand what to enter.',
        category: 'ux',
        impact: 'medium',
        confidence: 80,
        properties: { placeholder: this.suggestPlaceholderText(component.type, props.label) },
        reasoning: 'Placeholder text provides context and examples, improving user experience and reducing form abandonment.',
      });
    }

    // Botão sem texto de ação claro
    if (component.type === 'button' && (!props.text || props.text.length < 3)) {
      suggestions.push({
        id: `ux-button-text-${component.id}`,
        title: 'Improve Button Text',
        description: 'Button text should clearly describe the action.',
        category: 'ux',
        impact: 'high',
        confidence: 90,
        properties: { text: this.suggestButtonText(props.actionType || 'custom') },
        reasoning: 'Clear, action-oriented button text helps users understand what will happen when they click.',
      });
    }

    // Formulários grandes sem indicação de progresso
    if (context.formContext.totalComponents > 8 && component.type === 'button' && props.actionType === 'save') {
      suggestions.push({
        id: `ux-loading-state-${component.id}`,
        title: 'Add Loading State',
        description: 'Show loading state for better user feedback on form submission.',
        category: 'ux',
        impact: 'medium',
        confidence: 75,
        properties: { loading: false }, // Isso seria gerenciado pela lógica do formulário
        reasoning: 'Loading states prevent users from clicking multiple times and provide feedback during processing.',
      });
    }

    return suggestions;
  }

  private generateValidationSuggestions(component: BuilderNode, context: ComponentContext): PropertySuggestion[] {
    const suggestions: PropertySuggestion[] = [];
    const props = component.props as any;

    // Campo de email sem validação
    if (component.type === 'text' && 
        props.label?.toLowerCase().includes('email') && 
        !props.pattern) {
      suggestions.push({
        id: `validation-email-${component.id}`,
        title: 'Add Email Validation',
        description: 'This appears to be an email field but lacks validation.',
        category: 'validation',
        impact: 'high',
        confidence: 90,
        properties: { 
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          required: true 
        },
        reasoning: 'Email validation prevents invalid submissions and improves data quality.',
      });
    }

    // Campo numérico sem limites
    if (component.type === 'number' && (!props.min && !props.max)) {
      suggestions.push({
        id: `validation-number-limits-${component.id}`,
        title: 'Set Number Limits',
        description: 'Number inputs should have reasonable min/max values.',
        category: 'validation',
        impact: 'medium',
        confidence: 70,
        properties: { min: 0, max: 999999 },
        reasoning: 'Setting limits prevents invalid data entry and improves user experience.',
      });
    }

    return suggestions;
  }

  private generatePerformanceSuggestions(component: BuilderNode, context: ComponentContext): PropertySuggestion[] {
    const suggestions: PropertySuggestion[] = [];

    // Muitos componentes sem virtualização
    if (context.formContext.totalComponents > 20) {
      suggestions.push({
        id: `performance-virtualization-${component.id}`,
        title: 'Consider Form Optimization',
        description: 'Large forms may benefit from progressive disclosure or pagination.',
        category: 'performance',
        impact: 'medium',
        confidence: 60,
        properties: {},
        reasoning: 'Large forms can be overwhelming and slow. Consider breaking them into steps or sections.',
      });
    }

    return suggestions;
  }

  private generateStyleSuggestions(component: BuilderNode, context: ComponentContext): PropertySuggestion[] {
    const suggestions: PropertySuggestion[] = [];
    const props = component.props as any;
    const userPreferences = this.userBehavior.value.preferredStyles;

    // Sugere estilo consistente baseado nos padrões do usuário
    if (userPreferences.backgroundColor && !props.backgroundColor) {
      suggestions.push({
        id: `style-consistency-${component.id}`,
        title: 'Apply Consistent Styling',
        description: 'Use your preferred color scheme for consistency.',
        category: 'improvement',
        impact: 'low',
        confidence: 60,
        properties: { 
          backgroundColor: userPreferences.backgroundColor,
          textColor: userPreferences.textColor || '#000000'
        },
        reasoning: 'Consistent styling creates a more professional and cohesive user interface.',
      });
    }

    return suggestions;
  }

  private calculateSuggestionScore(suggestion: PropertySuggestion): number {
    const impactWeights = { high: 3, medium: 2, low: 1 };
    const impactScore = impactWeights[suggestion.impact];
    return (suggestion.confidence * impactScore) / 100;
  }

  private generateCacheKey(component: BuilderNode, context: ComponentContext): string {
    return `${component.type}-${component.id}-${JSON.stringify(component.props).slice(0, 100)}`;
  }

  private suggestLabelText(componentType: string): string {
    const suggestions = {
      text: 'Enter text',
      textarea: 'Enter description',
      number: 'Enter number',
      email: 'Email address',
    };
    return suggestions[componentType as keyof typeof suggestions] || 'Label';
  }

  private suggestPlaceholderText(componentType: string, label?: string): string {
    if (label?.toLowerCase().includes('email')) return 'example@domain.com';
    if (label?.toLowerCase().includes('phone')) return '(555) 123-4567';
    if (label?.toLowerCase().includes('name')) return 'Enter your name';
    
    const suggestions = {
      text: 'Type here...',
      textarea: 'Enter detailed information...',
      number: '0',
    };
    return suggestions[componentType as keyof typeof suggestions] || 'Enter value';
  }

  private suggestButtonText(actionType: string): string {
    const suggestions = {
      save: 'Save Changes',
      cancel: 'Cancel',
      custom: 'Click Here',
    };
    return suggestions[actionType as keyof typeof suggestions] || 'Submit';
  }

  private calculateColorContrast(bg: string, text: string): number {
    // Cálculo de contraste simplificado - na implementação real, use biblioteca de cores adequada
    return Math.random() * 10; // Implementação simulada
  }

  private suggestBetterColors(bg: string, text: string): Record<string, string> {
    // Sugestão de cor simplificada - na implementação real, use teoria de cores adequada
    return {
      backgroundColor: '#ffffff',
      textColor: '#000000',
    };
  }

  private trackUserAction(action: string) {
    const current = this.userBehavior.value;
    const updatedActions = [action, ...current.recentActions].slice(0, 50);
    
    this.userBehavior.next({
      ...current,
      recentActions: updatedActions,
    });
    
    this.saveUserBehavior();
  }

  private loadUserBehavior() {
    const saved = localStorage.getItem('ai-suggestions-user-behavior');
    if (saved) {
      try {
        this.userBehavior.next(JSON.parse(saved));
      } catch {
        // Usa valores padrão
      }
    }
  }

  private saveUserBehavior() {
    localStorage.setItem('ai-suggestions-user-behavior', JSON.stringify(this.userBehavior.value));
  }
}
